<template>
  <div class="sections-holder">
    <FaqSection>
      <span slot="heading">
        What is a normal kurtosis value?
      </span>
      <div slot="content">
        <p>A normal distribution typically has a kurtosis value of 3.</p>
        <p>But it's important to note that not all distributions are normal, and so their kurtosis values may differ. In fact, it is common for real-world distributions to exhibit some degree of kurtosis, which means that they are either more peaked or flatter than a normal distribution.</p>
        <p>Kurtosis values greater than 3 indicate a distribution with a higher peak than a normal distribution, while kurtosis values less than 3 indicate a flatter one.</p>
        <p>It's worth noting that interpreting kurtosis values can be tricky. It's important to always consider the shape of the distribution as a whole, rather than relying solely on kurtosis to make inferences about the data.</p>
      </div>
    </FaqSection>
    <hr>
    <FaqSection>
      <span slot="heading">
        How to calculate kurtosis in Excel?
      </span>
      <div slot="content">
        <p>Kurtosis can be easily calculated in Excel using the KURT function:</p>
        <p class="center-bold">
          KURT(number1, [number2], ...)
        </p>
        <p>The function requires only the cell range containing the data, for example the following dataset:</p>
        <img src="../../../../assets/images/calculators/kurtosis/kurtosis-faq-01-desktop.png" alt="Dataset image.">
        <p>We type KURT, then select the data contained in the range (C5:C14):</p>
        <img src="../../../../assets/images/calculators/kurtosis/kurtosis-faq-02-desktop.png" alt="KURT in the range (C5:C14).">
        <img class="no-top-margin" src="../../../../assets/images/calculators/kurtosis/kurtosis-faq-03-desktop.png" alt="The result is -1.12.">
        <p>The result is -1.12.</p>
        <p>Note that Excel calculates the sample excess kurtosis, not the sample kurtosis. The function uses the following formula:</p>
        <p class="formulas-overflow">
          \[K=\left\{\frac{n(n+1)}{(n-1)(n-2)(n-3)}\sum_{i=1}^{n}\left(\frac{X_i-\bar{X}}{s}\right)^4\right\}-\frac{3{(n-1)}^2}{(n-2)(n-3)}\]
        </p>
        <p>The last term is deducted from the equation to make the kurtosis of a normal distribution equal to 0. To calculate the sample kurtosis, you have to add it back using the following formula:</p>
        <p class="formulas-overflow">
          \[Sample\ Kurtosis\ =\ Sample\ Excess\ Kurtosis\ + \frac{3{(n-1)}^2}{(n-2)(n-3)}\]
        </p>
        <p>Meanwhile, Excel does not have a built-in function that you can use to calculate the population excess kurtosis. To obtain it, you have to use the following formula:</p>
        <p class="formulas-overflow">
          \[\gamma_2=\frac{1}{N}\sum_{i=1}^{n}\left(\frac{X_i-\bar{\mu}}{\sigma}\right)^4-3\]
        </p>
        <p>Alternatively, if you need to calculate the population kurtosis, you have to omit the last term from the pervious equation:</p>
        <p class="formulas-overflow">
          \[\gamma_2=\frac{1}{N}\sum_{i=1}^{n}\left(\frac{X_i-\bar{\mu}}{\sigma}\right)^4\]
        </p>
        <p>Whichever calculation method you chose, make sure you double-check your result using the Kurtosis Calculator above.</p>
      </div>
    </FaqSection>
  </div>
</template>

<script>
import FaqSection from './Section.vue';

export default {
  components: {
    FaqSection,
  },
};
</script>

<style lang="scss" scoped>
.sections-holder {
  hr {
    margin: var(--spacing-8) 0 var(--spacing-24);
    border: 1px solid rgba(0, 12, 31, 0.12);
  }

  .faq-holder {
    .center-bold {
      text-align: center;
      font-weight: 600;
      color: var(--main-dark);
    }
  }
}
</style>
